import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Box,
  Field,
  FlexControlledForm,
  Preloader,
} from '@common-fe/common-fe';
import * as yup from 'yup';

import AppButton from '@/components/controls/AppButton';
import { WarnModal } from '@/components/elements';

import { OrganizationName } from '../AddClaimProcessorAndTeamButton/MultipleOrganizationSelector';
import { ClaimsTeam } from '../ClaimsProcessors.types';

import { useCreateClaimsTeamFields } from './hooks/useCreateClaimsTeamFields';
import useGetAllServicesQuery, { Service } from './MultipleServiceSelector/useGetAllServices.query';
import useCreateClaimsTeamQuery from './queries/useCreateClaimsTeam.query';
import useGetAlreadyExistClaimsTeamsQuery, { Intersection } from './queries/useGetAlreadyExistClaimsTeams.query';
import useUpdateClaimsTeamQuery from './queries/useUpdateClaimsTeam.query';
import ClaimsTeamNotification from './ClaimsTeamNotification';

const insertOrgName = (id?: string, names?: OrganizationName[]) => ({
  id,
  name: names?.find((item) => item?.id === id)?.name,
});
const extendService = (id?: string, services?: Service[]) => {
  const { name, categoryType } = services?.find((service) => service?.id === id) || {};

  return {
    id,
    name,
    categoryType,
  };
};

const insertNamesIntoIntersections = (
  data?: Intersection[],
  services?: Service[],
  organizationsNames?: OrganizationName[],
) => data?.map((intersection) => ({
  ...intersection,
  intersectionOrganizations: intersection?.intersectionOrganizations?.map((item) => insertOrgName(item?.id, organizationsNames)),
  intersectionServices: intersection?.intersectionServices?.map((item) => extendService(item?.id, services)),
}));

interface Props {
  onCancel?: (value: boolean) => void;
  editableTeam?: ClaimsTeam;
  onSuccess?: (id?: string) => void;
}

const CreateClaimsTeamModalForm: React.FC<Props> = ({
  onCancel,
  editableTeam,
  onSuccess,
}) => {
  const [intersections, setIntersections] = useState<Intersection[] | undefined>();
  const [formValues, setFormValues] = useState<{teamName: string}>();
  const [errorModal, setErrorModal] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [createTeamSetup, setCreateTeamSetup] = useState(false);
  const [organizationsNames, setOrganizationsNames] = useState<OrganizationName[]>([]);
  const organizationsIds = useMemo(() => organizationsNames.map((item) => item.id || ''), [organizationsNames]);
  const { formattedData: services } = useGetAllServicesQuery();
  const [servicesIds, setServicesIds] = useState<string[]>([]);
  const areAllServicesSelected = useMemo(() => servicesIds.length === services.length, [servicesIds, services]);
  const [isEditLoading, setIsEditLoading] = useState(false);

  const { create, isLoading } = useCreateClaimsTeamQuery();
  const { update, isLoading: isUpdateLoading } = useUpdateClaimsTeamQuery(editableTeam?.id);

  const createTeamFields = useCreateClaimsTeamFields({
    allServices: services,
    setOrganizationsNames,
    editableTeam,
    setServicesIds,
    servicesIds,
    isSubmit,
  });

  const changeValues = useCallback((
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: any,
    fields: Field[],
    setFieldsAreFilled: (val: boolean) => void,
  ) => {

    setFormValues((
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      prev: any,
    ) => ({ ...prev, ...values }));

    try {
      const validatorMap = fields.reduce((map, field) => ({
        ...map,
        [field.name]: field.validator,
      }), {});
      const schema = yup.object()
        .shape(validatorMap);
      schema.validateSync(values, { abortEarly: false });
      setFieldsAreFilled(true);
    } catch {
      setFieldsAreFilled(false);
    }
  }, []);

  const handleChangeFormValues = useCallback((values: object) => {
    changeValues(values, createTeamFields, setCreateTeamSetup);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTeamFields]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel(false);
    }
  }, [onCancel]);

  const assignedOrganizationIdsFormatter = (ids: string[]) => {
    if (ids) {
      return ids.map((id) => +id);
    }
    return [];
  };

  const { getAlreadyExistsTeams } = useGetAlreadyExistClaimsTeamsQuery();
  
  const handleSubmit = useCallback(async () => {
    setIsSubmit(true);

    if (createTeamSetup) {
      if (!servicesIds?.length || !organizationsIds?.length) return;
      try {
        const response = await getAlreadyExistsTeams({
          name: formValues?.teamName as string,
          servicesIds: assignedOrganizationIdsFormatter(servicesIds),
          organizationsIds: assignedOrganizationIdsFormatter(organizationsIds),
          isGenericClaimsTeam: areAllServicesSelected,
        });
        const data = response?.filter((item) => item.teamId !== editableTeam?.id);

        if (data.length) {
          return setIntersections(insertNamesIntoIntersections(data, services, organizationsNames));
        }

        setIntersections([]);

        if (editableTeam) {
          const justUpdatedTeam = await update({
            name: formValues?.teamName as string,
            allowedServices: servicesIds,
            assignedOrganizationIds: assignedOrganizationIdsFormatter(organizationsIds),
            isGenericClaimsTeam: areAllServicesSelected,
          });
          const preparedTeamId = justUpdatedTeam?.id ? `${justUpdatedTeam.id}` : undefined;
          onSuccess && onSuccess(preparedTeamId);
          handleCancel();
          return;
        }

        const justCreatedTeam = await create({
          name: formValues?.teamName as string,
          allowedServices: servicesIds,
          assignedOrganizationIds: assignedOrganizationIdsFormatter(organizationsIds),
          isGenericClaimsTeam: areAllServicesSelected,
        });
        const preparedTeamId = justCreatedTeam?.id ? `${justCreatedTeam.id}` : undefined;
        onSuccess && onSuccess(preparedTeamId);
        handleCancel();
      }
      catch {
        setErrorModal(true);
      }
    }
  }, [
    services,
    organizationsNames,
    getAlreadyExistsTeams,
    areAllServicesSelected,
    servicesIds,
    create,
    createTeamSetup,
    formValues,
    handleCancel,
    organizationsIds,
    onSuccess,
    update,
    editableTeam,
  ]);

  useEffect(() => {
    if (editableTeam) {
      setServicesIds(editableTeam?.allowedServices || []);
    }
  }, [editableTeam]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (editableTeam) {
      setIsEditLoading(true);
      timer = setTimeout(() => {
        setIsEditLoading(false);
      }, 500);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [editableTeam]);

  return (
    <Box data-testid="Create_claims_team-modal-form">
      <WarnModal
        testId="new-claims-team-error-modal"
        visible={errorModal}
        onSetVisible={setErrorModal}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot save the Claims team with the latest records.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={handleSubmit}
        submitButtonText="Try again"
      />
      <Box direction="column">
        <Box round="container1Round" pad="spacing24" background="module">
          {isEditLoading
            ? <Preloader />
            : (
              <Box data-testid="Create_claims_team-form" round="container1Round" background="canvas">
                <FlexControlledForm
                  fields={createTeamFields}
                  editMode
                  showError={isSubmit}
                  onChangeValues={handleChangeFormValues}
                  isModalType
                />
              </Box>
            )}
        </Box>
        <ClaimsTeamNotification
          intersections={intersections}
          amountOfAllServices={services.length}
        />
        <Box
          direction="row"
          fill="horizontal"
          margin={{ top: 'spacing24' }}
          align="end"
          justify="end"
          width="medium"
          gap="xs"
        >
          <AppButton
            testId="Create_claims_team-cancel"
            buttonType="secondary"
            width="control"
            type="button"
            onClick={handleCancel}
          >
            Cancel
          </AppButton>
          <AppButton
            testId="Create_claims_team-add"
            type="button"
            disabled={isLoading || isUpdateLoading}
            onClick={handleSubmit}
            width="control"
          >
            {isLoading || isUpdateLoading
              ? (<Preloader />)
              : 'Confirm'}
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateClaimsTeamModalForm;
